import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Keyboard, Command } from 'lucide-react';
import { formatShortcut, type KeyboardShortcut } from '@/hooks/useKeyboardShortcuts';

interface KeyboardShortcutsDialogProps {
  open: boolean;
  onClose: () => void;
  shortcuts?: KeyboardShortcut[];
}

const DEFAULT_SHORTCUTS: Array<{ category: string; shortcuts: Partial<KeyboardShortcut>[] }> = [
  {
    category: 'General',
    shortcuts: [
      { key: 's', ctrl: true, description: 'Save workflow' },
      { key: 'Enter', ctrl: true, description: 'Execute workflow' },
      { key: 'a', ctrl: true, shift: true, description: 'Open AI Assistant' },
      { key: '?', description: 'Show keyboard shortcuts' },
      { key: 'Escape', description: 'Close dialog / Deselect' },
    ],
  },
  {
    category: 'Canvas',
    shortcuts: [
      { key: 'a', ctrl: true, description: 'Select all nodes' },
      { key: 'c', ctrl: true, description: 'Copy selected nodes' },
      { key: 'x', ctrl: true, description: 'Cut selected nodes' },
      { key: 'v', ctrl: true, description: 'Paste nodes' },
      { key: 'd', ctrl: true, description: 'Duplicate selected nodes' },
      { key: 'Delete', description: 'Delete selected nodes' },
      { key: 'Backspace', description: 'Delete selected nodes' },
    ],
  },
  {
    category: 'View',
    shortcuts: [
      { key: '+', ctrl: true, description: 'Zoom in' },
      { key: '-', ctrl: true, description: 'Zoom out' },
      { key: '0', ctrl: true, description: 'Reset zoom / Fit view' },
      { key: '`', ctrl: true, description: 'Toggle execution panel' },
    ],
  },
  {
    category: 'Node Editor',
    shortcuts: [
      { key: 'Enter', description: 'Save and close' },
      { key: 'Escape', description: 'Close without saving' },
      { key: 't', ctrl: true, description: 'Test node' },
      { key: 'e', ctrl: true, description: 'Open expression builder' },
    ],
  },
  {
    category: 'History',
    shortcuts: [
      { key: 'z', ctrl: true, description: 'Undo' },
      { key: 'z', ctrl: true, shift: true, description: 'Redo' },
    ],
  },
];

export const KeyboardShortcutsDialog: React.FC<KeyboardShortcutsDialogProps> = ({
  open,
  onClose,
  shortcuts,
}) => {
  const shortcutGroups = shortcuts
    ? [{ category: 'Available Shortcuts', shortcuts }]
    : DEFAULT_SHORTCUTS;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Keyboard className="h-5 w-5" />
            <DialogTitle>Keyboard Shortcuts</DialogTitle>
          </div>
          <DialogDescription>
            Use these keyboard shortcuts to work faster and more efficiently
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh]">
          <div className="space-y-6 pr-4">
            {shortcutGroups.map((group) => (
              <div key={group.category}>
                <h3 className="font-semibold mb-3 text-sm text-muted-foreground uppercase tracking-wide">
                  {group.category}
                </h3>
                <div className="space-y-2">
                  {group.shortcuts.map((shortcut, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <span className="text-sm">{shortcut.description}</span>
                      <div className="flex items-center gap-1">
                        {formatShortcut(shortcut)
                          .split(' + ')
                          .map((key, i, arr) => (
                            <React.Fragment key={i}>
                              <Badge
                                variant="outline"
                                className="font-mono text-xs px-2 py-0.5"
                              >
                                {key === 'Ctrl' && navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? (
                                  <Command className="h-3 w-3" />
                                ) : (
                                  key
                                )}
                              </Badge>
                              {i < arr.length - 1 && (
                                <span className="text-muted-foreground text-xs">+</span>
                              )}
                            </React.Fragment>
                          ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        <div className="flex items-center gap-2 text-xs text-muted-foreground pt-4 border-t">
          <Keyboard className="h-3 w-3" />
          <span>Press <Badge variant="outline" className="text-xs px-1.5 py-0">?</Badge> anytime to view shortcuts</span>
        </div>
      </DialogContent>
    </Dialog>
  );
};
